// INVARIANT: the whole source is HTML-escaped BEFORE any markup is added, so the
// only tags out are the ones written here; link hrefs are scheme-gated.

const ALLOWED_LINK_SCHEMES = ['http://', 'https://', 'mailto:'];

export const EXCERPT_CHARS = 240;

function toPlainText(markdown: string): string {
  return markdown
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+[.)]\s+/gm, '')
    .replace(/^\s*#{1,6}\s+/gm, '')
    .replace(/^\s*>\s?/gm, '')
    .replace(/^\s*(---+|\*\*\*+)\s*$/gm, ' ')
    .replace(/^\s*\|?[\s:|-]*\|[\s:|-]*$/gm, ' ')
    .replace(/\|/g, ' ')
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[*_`~]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function markdownExcerpt(source: string, maxChars: number = EXCERPT_CHARS): string {
  const body = source.replace(/^(?:\s*(?:#{1,6}\s+.*)?\r?\n)*/, '');
  const plain = toPlainText(body) || toPlainText(source);

  if (plain.length <= maxChars) return plain;
  const cut = plain.slice(0, maxChars);
  const sentence = cut.search(/[.!?](?=[^.!?]*$)/);
  if (sentence >= maxChars / 2) return cut.slice(0, sentence + 1);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

export function markdownParagraphs(source: string): string[] {
  const paragraphs: string[] = [];
  let block: string[] = [];

  const flush = (): void => {
    const text = toPlainText(block.join('\n'));
    if (text) paragraphs.push(text);
    block = [];
  };

  for (const line of source.replace(/```[\s\S]*?```/g, '').split(/\r?\n/)) {
    const ownLine = /^\s*([-*+]|\d+[.)])\s+/.test(line) || /^\s*#{1,6}\s+/.test(line);
    if (ownLine || line.trim() === '') flush();
    if (ownLine) {
      const text = toPlainText(line);
      if (text) paragraphs.push(text);
    } else if (line.trim() !== '') {
      block.push(line);
    }
  }
  flush();

  return paragraphs;
}

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** A fragment or same-origin path; `//host` and `/\host` are protocol-relative. */
function isInternalUrl(url: string): boolean {
  return url.startsWith('#') || /^\/(?![/\\])/.test(url);
}

function isSafeUrl(url: string): boolean {
  const lower = url.toLowerCase();
  return isInternalUrl(url) || ALLOWED_LINK_SCHEMES.some((scheme) => lower.startsWith(scheme));
}

function renderInline(escaped: string): string {
  const codeSpans: string[] = [];
  let out = escaped.replace(/`([^`]+)`/g, (_, code: string) => {
    codeSpans.push(code);
    return `\uE000${codeSpans.length - 1}\uE000`;
  });

  out = out
    .replace(/\[([^\]]*)\]\(([^)\s]+)\)/g, (whole, label: string, url: string) =>
      !isSafeUrl(url)
        ? whole
        : /^https?:/i.test(url)
          ? `<a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a>`
          : `<a href="${url}">${label}</a>`,
    )
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/__([^_]+)__/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/(^|\W)_([^_]+)_(?=\W|$)/g, '$1<em>$2</em>');

  return out.replace(/\uE000(\d+)\uE000/g, (_, i: string) => `<code>${codeSpans[Number(i)]}</code>`);
}

export function renderMarkdownInline(source: string): string {
  return renderInline(escapeHtml(source.replace(/\uE000/g, '')));
}

function tableCells(line: string): string[] {
  return line
    .trim()
    .replace(/^\||\|$/g, '')
    .split(/(?<!\\)\|/)
    .map((cell) => cell.replace(/\\\|/g, '|').trim());
}

// The pipe is required, so a plain `---` stays a horizontal rule.
function isTableDelimiter(line: string): boolean {
  return line.includes('|') && tableCells(line).every((cell) => /^:?-+:?$/.test(cell));
}

export function renderMarkdown(source: string): string {
  // U+E000 is the code-span sentinel; stripped so a document cannot forge one.
  const lines = escapeHtml(source.replace(/\uE000/g, '')).split(/\r?\n/);
  const html: string[] = [];

  let listTag: 'ul' | 'ol' | null = null;
  let inCode = false;
  let paragraph: string[] = [];

  const closeList = (): void => {
    if (listTag) {
      html.push(`</${listTag}>`);
      listTag = null;
    }
  };

  const closeParagraph = (): void => {
    if (paragraph.length > 0) {
      html.push(`<p>${renderInline(paragraph.join(' '))}</p>`);
      paragraph = [];
    }
  };

  const closeBlocks = (): void => {
    closeParagraph();
    closeList();
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (/^\s*```/.test(line)) {
      closeBlocks();
      html.push(inCode ? '</code></pre>' : '<pre><code>');
      inCode = !inCode;
      continue;
    }

    if (inCode) {
      html.push(line);
      continue;
    }

    if (line.trim() === '') {
      closeBlocks();
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      closeBlocks();
      const level = heading[1].length;
      html.push(`<h${level}>${renderInline(heading[2].trim())}</h${level}>`);
      continue;
    }

    if (/^\s*(---+|\*\*\*+)\s*$/.test(line)) {
      closeBlocks();
      html.push('<hr />');
      continue;
    }

    const quote = /^\s*&gt;\s?(.*)$/.exec(line);
    if (quote) {
      closeBlocks();
      html.push(`<blockquote>${renderInline(quote[1])}</blockquote>`);
      continue;
    }

    // The PDF conversion can emit an all-empty header row: a table without a header.
    if (line.includes('|') && i + 1 < lines.length && isTableDelimiter(lines[i + 1])) {
      closeBlocks();
      const header = tableCells(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && lines[i].includes('|')) {
        rows.push(tableCells(lines[i]));
        i++;
      }
      i--;

      const cell = (tag: 'th' | 'td', text: string): string => `<${tag}>${renderInline(text)}</${tag}>`;
      html.push('<table>');
      if (header.some((text) => text !== '')) {
        html.push(`<thead><tr>${header.map((text) => cell('th', text)).join('')}</tr></thead>`);
      }
      html.push(
        `<tbody>${rows.map((row) => `<tr>${row.map((text) => cell('td', text)).join('')}</tr>`).join('')}</tbody>`,
      );
      html.push('</table>');
      continue;
    }

    const bullet = /^\s*[-*+]\s+(.*)$/.exec(line);
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (bullet || numbered) {
      closeParagraph();
      const wanted = bullet ? 'ul' : 'ol';
      if (listTag !== wanted) {
        closeList();
        html.push(`<${wanted}>`);
        listTag = wanted;
      }
      html.push(`<li>${renderInline((bullet ?? numbered)![1])}</li>`);
      continue;
    }

    closeList();
    paragraph.push(line.trim());
  }

  if (inCode) html.push('</code></pre>');
  closeBlocks();

  return html.join('\n');
}
